import {
  GetPubliccmsContent,
  GetcmsContent,
  clearCmsApiResponseCaches,
} from "../Api/api";
import { fetchIpAddress } from "../ip";

interface IcmsPublicContent { 
  post_id: string; 
  lat: string; 
  log: string; 
}

const publicContentInFlight = new Map<string, Promise<string[]>>();
const publicContentCache = new Map<string, string[]>();
const privateContentInFlight = new Map<string, Promise<string[]>>();
const privateContentCache = new Map<string, string[]>();

// Persist full article content to sessionStorage so the blog detail page
// can render instantly on refresh (no re-fetch of the article body).
const CMS_CONTENT_CACHE_KEY = "ticca_cms_content_cache";
const CMS_CONTENT_MAX = 80; // keep last 80 articles to avoid bloat

function readCmsContentCache(): Map<string, string[]> {
  try {
    const raw = sessionStorage.getItem(CMS_CONTENT_CACHE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as [string, string[]][];
    return new Map(parsed);
  } catch {
    return new Map();
  }
}

function writeCmsContentCache(key: string, content: string[]): void {
  try {
    const cache = readCmsContentCache();
    cache.set(key, content);
    // Trim to keep only the most recent entries
    const entries = Array.from(cache.entries());
    if (entries.length > CMS_CONTENT_MAX) {
      entries.splice(0, entries.length - CMS_CONTENT_MAX);
    }
    sessionStorage.setItem(CMS_CONTENT_CACHE_KEY, JSON.stringify(entries));
  } catch {
    // ignore storage errors
  }
}

function buildPublicContentCacheKey({ post_id }: IcmsPublicContent): string {
  return `public_${post_id}`;
}

function buildPrivateContentCacheKey({ post_id }: IcmsPublicContent): string {
  return `private_${post_id}`;
}

export const cmsPublicContent = async ({ post_id, lat, log }: IcmsPublicContent) => {
  const key = buildPublicContentCacheKey({ post_id, lat, log });

  // 1. In-memory cache (fastest — survives SPA navigation)
  const cached = publicContentCache.get(key);
  if (cached) {
    return cached;
  }

  // 2. SessionStorage cache (survives page refresh)
  const stored = readCmsContentCache().get(key);
  if (stored) {
    publicContentCache.set(key, stored);
    return stored;
  }

  const inFlight = publicContentInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
  try {
    const response1 = await GetPubliccmsContent({
      ip_address: await fetchIpAddress(),
      form_lat: lat,
      form_lon: log,
      post_id: post_id,
    });

    const content = response1.data?.content?.split("<!--nextpage-->") || [];
    publicContentCache.set(key, content);
    writeCmsContentCache(key, content);
    return content;
  } catch (error) {
    return [];
  } finally {
    publicContentInFlight.delete(key);
  }
  })();

  publicContentInFlight.set(key, request);
  return request;
};

export const cmsPrivateContent = async ({ post_id, lat, log }: IcmsPublicContent) => {
  const key = buildPrivateContentCacheKey({ post_id, lat, log });

  const cached = privateContentCache.get(key);
  if (cached) {
    return cached;
  }

  const inFlight = privateContentInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
  try {
    const response1 = await GetcmsContent({
      ip_address: await fetchIpAddress(),
      form_lat: lat,
      form_lon: log,
      post_id: post_id,
    });

    const content = response1.data?.content?.split("<!--nextpage-->") || [];
    privateContentCache.set(key, content);
    return content;
  } catch (error) {
    return [];
  } finally {
    privateContentInFlight.delete(key);
  }
  })();

  privateContentInFlight.set(key, request);
  return request;
};

export const clearCmsContentCaches = (): void => {
  publicContentCache.clear();
  publicContentInFlight.clear();
  privateContentCache.clear();
  privateContentInFlight.clear();
  clearCmsApiResponseCaches();
};
